// ADR 2.10.26f §3 — `parseSpec(kind, value)`: the schema check a script's
// result must pass. It runs in the sandbox worker **and again on the main
// thread** (§4d): the script shares the worker's realm and could have patched
// the worker's copy, so the main thread's parse is the one that counts.
//
// Shape and type only — exactly the spec's keys with their types, strings at
// most 2,048 characters, at most 64 items an array, at most 16 KiB as JSON.
// Content rules (scopes, URLs, event names, manifest limits) are the
// Backend's. The value is checked *after* a JSON round trip, so a getter
// cannot answer one thing to the check and another to the caller.

import type { Spec } from './lingara.v1'

export const MAX_SPEC_BYTES = 16 * 1024
/** A spec string's longest length; also a `lingara.param` label's (ADR 8.10.26f §2). */
export const MAX_STRING = 2048
/** A spec list's most items; also a `choice` setting's most choices (ADR 8.10.26f §2). */
export const MAX_ITEMS = 64
/** A script's error message, or a parse problem, is cut to this many characters wherever it is reported. */
export const DETAIL_MAX = 200

export type SpecKind = Spec['kind']

export type ParsedSpec = { ok: true; spec: Spec } | { ok: false; detail: string }

type FieldType = 'kind' | 'string' | 'strings' | 'boolean' | 'one' | 'texts' | 'manifest'

/** Each key's type; a key ending in `?` is optional. */
type Schema = Record<string, FieldType>

const MANIFEST: Schema = {
  manifest_version: 'one',
  default_locale: 'string',
  name: 'texts',
  description: 'texts',
  render_url: 'string',
  slots: 'strings',
  context: 'strings',
  scopes: 'strings',
  tutor_note: 'boolean',
}

const SCHEMAS: Record<SpecKind, Schema> = {
  client: { kind: 'kind', name: 'string', scopes: 'strings', 'redirectUris?': 'strings' },
  secret: { kind: 'kind', client: 'string' },
  webhook: { kind: 'kind', client: 'string', url: 'string', events: 'strings' },
  app: { kind: 'kind', client: 'string', manifest: 'manifest' },
}

type Wire = Record<string, unknown>

function isPlainObject(value: unknown): value is Wire {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isText(value: unknown): boolean {
  return typeof value === 'string' && value.length <= MAX_STRING
}

function isTextMap(value: unknown): boolean {
  return isPlainObject(value) && Object.keys(value).length <= MAX_ITEMS && Object.values(value).every(isText)
}

function isTextList(value: unknown): boolean {
  return Array.isArray(value) && value.length <= MAX_ITEMS && value.every(isText)
}

const CHECKS: Record<Exclude<FieldType, 'kind' | 'manifest'>, (value: unknown) => boolean> = {
  string: isText,
  strings: isTextList,
  boolean: (value) => typeof value === 'boolean',
  one: (value) => value === 1,
  texts: isTextMap,
}

/** The first problem with `row` against `schema`, or `null`. */
function problem(row: Wire, schema: Schema, kind: SpecKind): string | null {
  const required = Object.keys(schema).filter((key) => !key.endsWith('?'))
  const allowed = new Set(Object.keys(schema).map((key) => key.replace(/\?$/, '')))
  const unknown = Object.keys(row).find((key) => !allowed.has(key))
  if (unknown !== undefined) return `unknown key ${unknown}`
  const missing = required.find((key) => !(key in row))
  if (missing !== undefined) return `missing ${missing}`
  for (const [rawKey, type] of Object.entries(schema)) {
    const key = rawKey.replace(/\?$/, '')
    if (!(key in row)) continue
    const bad = fieldProblem(row[key], type, kind)
    if (bad !== null) return `${key} ${bad}`
  }
  return null
}

function fieldProblem(value: unknown, type: FieldType, kind: SpecKind): string | null {
  if (type === 'kind') return value === kind ? null : `must be "${kind}"`
  if (type === 'manifest') {
    if (!isPlainObject(value)) return 'must be a manifest'
    const inner = problem(value, MANIFEST, kind)
    return inner === null ? null : `.${inner}`
  }
  return CHECKS[type](value) ? null : `has the wrong type or is too long`
}

/** A plain JSON copy of `value`, or `null` when it is not JSON or is over 16 KiB. */
function jsonCopy(value: unknown): Wire | null {
  if (!isPlainObject(value)) return null
  try {
    const json = JSON.stringify(value)
    if (json === undefined || new TextEncoder().encode(json).length > MAX_SPEC_BYTES) return null
    const copy: unknown = JSON.parse(json)
    return isPlainObject(copy) ? copy : null
  } catch {
    return null
  }
}

export function parseSpec(kind: SpecKind, value: unknown): ParsedSpec {
  const copy = jsonCopy(value)
  if (copy === null) return { ok: false, detail: 'not a plain object of at most 16 KiB' }
  const bad = problem(copy, SCHEMAS[kind], kind)
  return bad === null ? { ok: true, spec: copy as unknown as Spec } : { ok: false, detail: bad }
}
